import { Feature, GeoJsonObject, GeometryObject } from "geojson";
import { Layer } from "leaflet";
import { useEffect, useState } from "react";
import { GeoJSON } from "react-leaflet";
import { kml } from "@tmcw/togeojson";
import { useRootStore } from "../models/Root";

const NWS_WARNINGS_URL = "/kml-live/NWSWarnings.kml";
const REFRESH_INTERVAL = 5 * 60 * 1000;

const fetchNwsWarnings = async (
  signal: AbortSignal
): Promise<GeoJsonObject | undefined> => {
  const url = `${NWS_WARNINGS_URL}?t=${Date.now()}`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const text = await res.text();
  const xml = new DOMParser().parseFromString(text, "text/xml");
  const parseError = xml.querySelector("parsererror");
  if (parseError) {
    throw new Error(`XML parse error: ${parseError.textContent}`);
  }
  try {
    return kml(xml);
  } catch {
    return undefined;
  }
};

const NwsWarningsLayer = () => {
  const [geoJson, setGeoJson] = useState<GeoJsonObject | undefined>();
  const [fetchKey, setFetchKey] = useState(0);
  const {
    layers: { setSelectedFeature },
  } = useRootStore();

  useEffect(() => {
    const controller = new AbortController();

    const update = () =>
      fetchNwsWarnings(controller.signal)
        .then((data) => {
          if (data) {
            setGeoJson(data);
            setFetchKey((k) => k + 1);
          }
        })
        .catch((e) => {
          if (e.name !== "AbortError") {
            console.error("NWS Warnings fetch failed:", e);
          }
        });

    update();
    const interval = setInterval(update, REFRESH_INTERVAL);
    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, []);

  const onEachFeature = (feature: Feature<GeometryObject>, layer: Layer) => {
    layer.on({
      click: () => setSelectedFeature(feature),
    });
  };

  if (!geoJson) return null;

  return (
    <GeoJSON
      key={fetchKey}
      data={geoJson}
      onEachFeature={onEachFeature}
    />
  );
};

export default NwsWarningsLayer;
